import { Input, Button } from '@fluentui/react-components'
import { Dismiss16Regular, Search20Regular } from '@fluentui/react-icons'
import type { PersonRow, ProblemFilter } from '../lib/personRows'

const ROLES = ['Owner', 'Admin', 'Member']

/** Search box + problem-filter buttons (Blocked/Unresolved/Not SSO/...) + role buttons (Owner/
 * Admin/Member, shown only when this service's rows actually carry a role) — identical on the
 * Licenses tab and the User links tab (see hooks/useProblemFilters.ts), so switching tabs never
 * loses the applied filter or shows a different set of options. */
export default function ProblemFilterBar(props: {
  rows: PersonRow[]
  searchText: string
  onSearchChange: (v: string) => void
  problemFilters: { value: ProblemFilter; label: string; predicate: (row: PersonRow) => boolean }[]
  problemFilter: ProblemFilter
  onProblemFilterChange: (v: ProblemFilter) => void
  roleFilter: string | null
  onRoleFilterChange: (v: string | null) => void
  hasRoles: boolean
}) {
  const {
    rows,
    searchText,
    onSearchChange,
    problemFilters,
    problemFilter,
    onProblemFilterChange,
    roleFilter,
    onRoleFilterChange,
    hasRoles,
  } = props

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '24px 0' }}>
      <Input
        contentBefore={<Search20Regular />}
        contentAfter={
          searchText && (
            <Button
              appearance="transparent"
              size="small"
              icon={<Dismiss16Regular />}
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
            />
          )
        }
        placeholder="Search by email or name..."
        value={searchText}
        onChange={(_e, d) => onSearchChange(d.value)}
        style={{ minWidth: 240 }}
      />
      {problemFilters.map(f => {
        const count = rows.filter(f.predicate).length
        // Problem categories are hidden at zero to avoid clutter — except "Linked", which is a
        // stable filter (find manually-linked accounts) rather than a problem count, so it must be
        // present on every service regardless of how many exist.
        if (f.value !== 'all' && f.value !== 'linked' && count === 0) return null
        return (
          <Button
            key={f.value}
            size="small"
            appearance={problemFilter === f.value ? 'primary' : 'outline'}
            onClick={() => onProblemFilterChange(f.value)}
          >
            {f.label} ({count})
          </Button>
        )
      })}
      {hasRoles &&
        ROLES.map(role => {
          const count = rows.filter(row => row.role === role).length
          if (count === 0) return null
          return (
            <Button
              key={role}
              size="small"
              appearance={roleFilter === role ? 'primary' : 'outline'}
              onClick={() => onRoleFilterChange(role)}
            >
              {role} ({count})
            </Button>
          )
        })}
    </div>
  )
}
